'use client';

import * as DS from '@aurorasolar/ds';
import { x } from '@xstyled/styled-components';
import { useId, useState } from 'react';

import { BatteryMode, HeatingFuel } from '../engine';

import { BatteryModeSelector } from './BatteryModeSelector';
import { RangeInput } from './RangeInput';

export type Refinements = {
  heatingFuel: HeatingFuel;
  homeSqft: number;
};

type Props = {
  batteryMode: BatteryMode;
  onBatteryModeChange: (mode: BatteryMode) => void;
  onChange: (patch: Partial<Refinements>) => void;
  refinements: Refinements;
  solarEnabled: boolean;
};

const FUEL_LABELS: Record<HeatingFuel, string> = {
  ELECTRIC_RESISTANCE: 'Electric',
  GAS: 'Gas',
  HEAT_PUMP: 'Heat pump',
  NONE: 'None',
  OIL: 'Oil',
};

const FUELS: HeatingFuel[] = ['GAS', 'ELECTRIC_RESISTANCE', 'OIL', 'HEAT_PUMP', 'NONE'];

export const RefineDetails = ({ batteryMode, onBatteryModeChange, onChange, refinements, solarEnabled }: Props) => {
  const [open, setOpen] = useState(false);
  const panelId = useId();

  return (
    <DS.Flex gap={3} textAlign="left" w="100%">
      <x.h3 m={0}>
        <x.button
          alignItems="center"
          aria-controls={panelId}
          aria-expanded={open}
          bg="transparent"
          border={0}
          color="uiCopy"
          cursor="pointer"
          display="inline-flex"
          gap={2}
          onClick={() => setOpen(!open)}
          p={0}
          type="button"
        >
          <DS.Text as="span" fontWeight="medium" text="body14">
            Fine-tune your simulation
          </DS.Text>
          {open ? <DS.IconChevronUp size={4} /> : <DS.IconChevronDown size={4} />}
        </x.button>
      </x.h3>

      {open ? (
        <DS.Flex gap={3} id={panelId} w="100%">
          <DS.Flex gap={4} pb={4} w={{ _: '100%', md: '45%' }}>
            <DS.Flex alignItems="baseline" flexDirection="row" justifyContent="space-between">
              <DS.Text as="label" color="uiHelperCopy" htmlFor="electrification-sqft" text="body12">
                Home size
              </DS.Text>
              <DS.Text text="body14">{refinements.homeSqft.toLocaleString()} sq ft</DS.Text>
            </DS.Flex>
            <RangeInput
              ariaLabel="Home size in square feet"
              id="electrification-sqft"
              max={4000}
              min={800}
              onChange={(homeSqft) => onChange({ homeSqft })}
              step={100}
              value={refinements.homeSqft}
            />
          </DS.Flex>

          <DS.Grid columnGap={6} gridTemplateColumns={{ _: '1fr', md: '1fr 1fr' }} rowGap={4} w="100%">
            <DS.Flex gap={2} minW={0}>
              <DS.Text color="uiHelperCopy" text="body12">
                How do you heat your home today?
              </DS.Text>
              <DS.SegmentedControl
                handleSegmentsSelected={(selectedSegments) => {
                  const selected = selectedSegments[0]?.key;
                  if (selected) {
                    onChange({ heatingFuel: selected });
                  }
                }}
                segmentGroupProps={{ id: 'heating-fuel' }}
                segments={FUELS.map((key) => ({ key, label: FUEL_LABELS[key] }))}
                selectedSegmentKeys={[refinements.heatingFuel]}
              />
            </DS.Flex>

            <DS.Flex gap={2} minW={0}>
              <DS.Text color="uiHelperCopy" text="body12">
                Battery mode
              </DS.Text>
              <BatteryModeSelector mode={batteryMode} onModeChange={onBatteryModeChange} solarEnabled={solarEnabled} />
            </DS.Flex>
          </DS.Grid>
        </DS.Flex>
      ) : null}
    </DS.Flex>
  );
};
